import {Component, HostListener, Inject, NgZone, OnDestroy, OnInit, ChangeDetectionStrategy} from '@angular/core';
import {TranslateUtils} from './_shared/translate-utils';
import {AppConstants, AppRoles, PermissionKeys} from './app.constants';
import {faBars, faBell, faChevronRight, faSignOutAlt, faTimes, faCircleInfo, faExclamationTriangle, faExclamationCircle} from '@fortawesome/free-solid-svg-icons';
import {BehaviorSubject, firstValueFrom, Subject, Subscription} from 'rxjs';
import {BaseAuthRequest} from '@azure/msal-common';
import {
    AlarmService,
    AlertService, BackofficeService, BannerDto, BannerInformationLevelDto, DocumentService, InformationService,
    PushRegistrationService,
    CustomerService
} from './_services/configuration-services';
import {MSAL_GUARD_CONFIG, MsalBroadcastService, MsalGuardConfiguration, MsalService} from '@azure/msal-angular';
import {AuthError, EventMessage, EventType, InteractionStatus} from '@azure/msal-browser';
import {filter, takeUntil} from 'rxjs/operators';
import {environment} from '../environments/environment';
import {CustomerConfigService} from './_shared/customer-config-service';
import {RolesService} from './_shared/roles-service';
import {getApp, getApps, initializeApp} from 'firebase/app';
import {getMessaging, getToken, isSupported, Messaging, MessagePayload, onMessage, Unsubscribe} from 'firebase/messaging';
import {NotificationsService} from './_shared/notifications.service';
import {ConfirmModalService} from './_shared/_components/confirm-modal/confirm-modal.component';
import {CommunicationChannelConfigService} from './_shared/communication-channel-config.service';
import {BackofficePermissionsService} from './_shared/backoffice-permissions.service';
import {CustomerContextService} from './_shared/customer-context.service';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {PushNotificationModalComponent} from './_shared/_components/push-notification-modal/push-notification-modal.component';
import {Router} from '@angular/router';
import {SwUpdate} from '@angular/service-worker';
import {TranslateService} from '@ngx-translate/core';
import * as fileSaver from 'file-saver';
import {NGXLogger} from 'ngx-logger';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AppComponent implements OnInit, OnDestroy {

    private alarmRefreshInterval: number;

    icons = {
        chevronRight: faChevronRight,
        signOut: faSignOutAlt,
        cross: faTimes,
        menu: faBars,
        alertNotification: faBell,
        info: faCircleInfo,
        warning: faExclamationTriangle,
        incident: faExclamationCircle
    };

    AppRoles = AppRoles;
    PermissionKeys = PermissionKeys;
    BannerInformationLevelDto = BannerInformationLevelDto;

    env = environment;

    isIframe = false;
    isExpanded = false;
    isMobile = false;
    loggedIn = false;
    isAlarmTriggering = false;
    isAlertTriggering = false;
    hasUnreadMessage = false;

    activeBanner?: BannerDto = null;

    envName = '';
    accountDisplayName = '';
    accountEmail: string = null; // Used for push notifications

    innerWidth = 0;
    maxWidthAllowed = 993;

    currentLanguage = TranslateUtils.defaultLanguage;

    private readonly customerConfigLoadedSubscription: Subscription;
    private hasUnreadMessageSubscription: Subscription;
    private loggedInSubscription: Subscription;

    private messaging: Messaging | null = null;
    private pushMessagesUnsubscribe: Unsubscribe | null = null;

    private readonly loggedInSubject$ = new BehaviorSubject(false);
    private readonly loggedInMessage = this.loggedInSubject$.asObservable();

    private readonly destroying$ = new Subject<void>();

    constructor(private readonly translateUtils: TranslateUtils,
                @Inject(MSAL_GUARD_CONFIG) private readonly msalGuardConfig: MsalGuardConfiguration,
                private readonly authService: MsalService,
                private readonly msalBroadcastService: MsalBroadcastService,
                private readonly backofficeService: BackofficeService,
                private readonly backofficePermissionsService: BackofficePermissionsService,
                private readonly customerContextService: CustomerContextService,
                private readonly customerService: CustomerService,
                public readonly customerConfigService: CustomerConfigService,
                public readonly communicationChannelConfigService: CommunicationChannelConfigService,
                private readonly alarmService: AlarmService,
                private readonly alertService: AlertService,
                private readonly rolesService: RolesService,
                private readonly translateService: TranslateService,
                private readonly notificationService: NotificationsService,
                private readonly pushRegistrationService: PushRegistrationService,
                private readonly modalService: NgbModal,
                private readonly router: Router,
                private readonly ngZone: NgZone,
                private readonly updates: SwUpdate,
                private readonly documentService: DocumentService,
                private readonly informationService: InformationService,
                private readonly log: NGXLogger) {
    }

    ngOnInit(): void {
        this.innerWidth = window.innerWidth;
        this.isMobile = this.innerWidth < this.maxWidthAllowed;
        this.isIframe = window !== window.parent && !window.opener;
        this.envName = environment.envName;
        this.currentLanguage = this.translateUtils.getLanguage();

        this.manageAppUpdate();

        this.hasUnreadMessageSubscription = this.communicationChannelConfigService.unreadMessage.subscribe(hasUnread => {
            if (this.loggedIn) {
                this.hasUnreadMessage = hasUnread;
            }
        });

        this.loggedInSubscription = this.loggedInMessage.subscribe(loggedIn => {
            if (this.loggedIn !== loggedIn) { // Handle state changes
                this.loggedIn = loggedIn;

                this.clearAlarmPolling();

                if (loggedIn) {
                    const tokenClaims = this.authService.instance.getActiveAccount()?.idTokenClaims as any;

                    // Call the appropriate GetMe endpoint based on account type
                    const onGetMe = async (user: { firstName?: string; lastName?: string; email?: string }) => {
                        this.accountDisplayName = user ? `${user.firstName} ${user.lastName}` : null;
                        this.accountEmail = user?.email ?? null;

                        this.communicationChannelConfigService.fetchContactPageAccess();
                        this.communicationChannelConfigService.fetchHasUnreadMessage();
                        this.fetchHasAlarmOrAlert();
                        this.configureAlarmPolling();
                        await this.fetchInformationBanner();
                    };

                    if (this.rolesService.isBackofficeAccount()) {
                        this.backofficeService.getMe().pipe().subscribe(user => {
                            this.backofficePermissionsService.setCurrentBackofficeId(user.id);
                            this.backofficePermissionsService.setPermissions(
                                (user.permissions ?? []).map(p => p.key)
                            );
                            onGetMe(user);
                        });
                    } else {
                        this.customerService.getMe().pipe().subscribe(customer => {
                            // Cache the real Customer.Id (≠ token ADB2CObjectId for dissociated accounts) so
                            // ownership checks compare against the persisted id, not the claim.
                            this.customerContextService.setCurrentCustomerId(customer.id ?? null);
                            onGetMe(customer);
                        });
                    }
                }
            }
        });

        this.checkIfLoggedIn();

        // Handle B2C redirects
        this.msalBroadcastService.inProgress$
            .pipe(
                filter((status: InteractionStatus) => status === InteractionStatus.None),
                takeUntil(this.destroying$)
            )
            .subscribe(() => {
                this.checkIfLoggedIn();
                this.managePushSubscription();
            });

        this.msalBroadcastService.msalSubject$
            .pipe(
                filter((msg: EventMessage) => {
                    return msg.eventType === EventType.LOGIN_FAILURE || msg.eventType === EventType.ACQUIRE_TOKEN_FAILURE;
                }),
                takeUntil(this.destroying$)
            )
            .subscribe((result: EventMessage) => {
                if (result.eventType === EventType.ACQUIRE_TOKEN_FAILURE) {
                    // Clear B2C items from local storage to prevent unexpeced live lock
                    for (let i = 0; i < localStorage.length; i++) {
                        if (localStorage.getItem(localStorage.key(i)).indexOf('b2clogin') > 0) {
                            localStorage.removeItem(localStorage.key(i));
                        }
                    }

                } else {
                    const authError = result.error as AuthError;
                    // Learn more about AAD error codes at https://docs.microsoft.com/azure/active-directory/develop/reference-aadsts-error-codes
                    if (authError.message.includes('AADB2C90118')) { // Reset Password flow
                        // login request with reset authority
                        const resetPasswordFlowRequest: any = {
                            authority: `https://${environment.b2cDomainName}.b2clogin.com/${environment.b2cDomainName}.onmicrosoft.com/${environment.b2cResetPasswordPolicy}`,
                        };
                        this.login(resetPasswordFlowRequest);
                    }
                }
            });

        this.msalBroadcastService.msalSubject$
            .pipe(
                filter((msg: EventMessage) => msg.eventType === EventType.INITIALIZE_END),
                takeUntil(this.destroying$)
            )
            .subscribe(async () => {

                // Fix VNSG-3329 issue if the token has expired - refresh the token if idTokenClaims is not defined
                const accounts = this.authService.instance.getAllAccounts();

                if (accounts.length > 0) {
                    if (!accounts[0].idTokenClaims) {

                        const tryToRefreshIdTokenClaimsCounter  = sessionStorage.getItem('refreshIdTokenCounter');

                        if (tryToRefreshIdTokenClaimsCounter === null) {
                            sessionStorage.setItem('refreshIdTokenCounter', '1');

                            try {
                                await this.authService.instance.acquireTokenSilent({
                                    account: this.authService.instance.getAllAccounts()[0],
                                    scopes: []
                                });
                            } catch (error) {
                                this.log.error('An error occurred', error);
                            } finally {
                                window.location.reload();
                            }

                        }
                    }
                }

            });
    }

    ngOnDestroy(): void {
        this.destroying$.next(null);
        this.destroying$.complete();

        this.customerConfigLoadedSubscription.unsubscribe();
        this.hasUnreadMessageSubscription.unsubscribe();
        this.pushMessagesUnsubscribe?.();
        this.loggedInSubscription.unsubscribe();

        this.loggedInSubject$.complete();

        this.clearAlarmPolling();
    }

    login(userFlowRequest?: BaseAuthRequest): void {
        this.msalBroadcastService.inProgress$
            .pipe(
                filter((status: InteractionStatus) => status === InteractionStatus.None),
                takeUntil(this.destroying$)
            )
            .subscribe(() => {
                if (this.msalGuardConfig.authRequest) {
                    this.authService.loginRedirect({...this.msalGuardConfig.authRequest, ...userFlowRequest});
                } else {
                    this.authService.loginRedirect(userFlowRequest);
                }
            });
    }

    logout(): void {
        this.authService.logout();
        this.loggedInSubject$.next(false);
    }

    isLanguageAvailable(lang: string): boolean {
        return this.customerConfigService.isLanguageAvailable(lang);
    }

    changeLanguage(lang: string): void {
        this.currentLanguage = lang;
        this.translateUtils.setLanguage(lang);
    }

    toggleMobileMenu(): void {
        if (this.isMobile) {
            this.isExpanded = !this.isExpanded;
        }
    }

    @HostListener('window:resize', ['$event'])
    onResize(_): void {
        this.innerWidth = window.innerWidth;
        this.isMobile = this.innerWidth < this.maxWidthAllowed;
    }

    getTransactionRootRoute(): Array<string> {
        return (this.backofficePermissionsService.hasPermission(PermissionKeys.gestionTransactions) || this.rolesService.hasRoleCustomer()) ?
            ['transactions'] : ['transactions', 'search'];
    }

    canAccessTransactionsMenu(): boolean {
        return this.backofficePermissionsService.hasPermission(PermissionKeys.gestionTransactions) || this.rolesService.hasRoleCustomer();
    }

    canAccessCommunicationMenu(): boolean {
        return this.backofficePermissionsService.hasPermission(PermissionKeys.gestionCommunication) || this.rolesService.hasRoleCustomer();
    }

    downloadInstructionsManual() {
        this.documentService.downloadInstructionsManualDocument().pipe().subscribe(response => {
            fileSaver.saveAs(response.data, response.fileName);
        });
    }

    private checkIfLoggedIn(): void {
        if (this.authService.instance.getAllAccounts().length > 0) {
            // Logged in
            const tokenClaims = this.authService.instance.getAllAccounts()[0].idTokenClaims;

            if (tokenClaims) {
                const tokenTenantCode = tokenClaims['extension_Tenant'] as string;
                const tokenHasAccessToAllTenants = tokenClaims['extension_HasAccessToAllTenants'] as boolean;

                if (environment.tenantCode === tokenTenantCode || tokenHasAccessToAllTenants) {
                    // Valid tenant
                    this.authService.instance.setActiveAccount(this.authService.instance.getAllAccounts()[0]);
                    this.loggedInSubject$.next(true);
                    return;

                } else {
                    setTimeout(() => { // Ensure the translation is loaded before displaying the message
                        this.notificationService.info({ message: 'error.invalidTenant' });
                    }, 0)
                }
            }
        }

        this.loggedInSubject$.next(false);
    }

    private fetchHasAlarmOrAlert(): void {
        if (this.loggedIn && this.customerConfigService.isAlarmsEnabled() &&
            (this.backofficePermissionsService.hasPermission(PermissionKeys.gestionAlertesAlarmes) ||
                this.backofficePermissionsService.hasPermission(PermissionKeys.configurationAlertesAlarmes))) {

            this.alarmService.hasActiveAlarm().pipe().subscribe(hasActiveAlarm => {
                this.isAlarmTriggering = hasActiveAlarm;
            });
            this.alertService.hasActiveAlert().pipe().subscribe(hasActiveAlert => {
                this.isAlertTriggering = hasActiveAlert;
            });
        }
    }

    private async fetchInformationBanner(): Promise<void> {
        this.activeBanner = await firstValueFrom(this.informationService.getActiveBanner());
    }

    private configureAlarmPolling(): void {
        if (!this.alarmRefreshInterval && this.customerConfigService.isAlarmsEnabled() &&
            (this.backofficePermissionsService.hasPermission(PermissionKeys.gestionAlertesAlarmes) ||
                this.backofficePermissionsService.hasPermission(PermissionKeys.configurationAlertesAlarmes))) {

            this.alarmRefreshInterval = setInterval(_ => {
                this.fetchHasAlarmOrAlert();
            }, 60000);
        }
    }

    private clearAlarmPolling(): void {
        if (this.alarmRefreshInterval) {
            clearInterval(this.alarmRefreshInterval);
            this.alarmRefreshInterval = null;
        }
    }

    private async managePushSubscription(): Promise<void> {
        if (!this.env.firebase['apiKey'] || !await isSupported()) {
            // We don't ask the permission if the browser does not support Push notification
            // Or if firebase is not configured
            return;
        }

        if (Notification.permission === 'granted') {
            if (this.loggedIn) {
                this.registerToken();

            } else {
                const email = localStorage.getItem(AppConstants.pushRegistrationEmailKey);
                if (email) {
                    this.registerToken();

                } else {
                    // Need to ask the permission to save the email
                    localStorage.setItem(AppConstants.pushPermissionAskedKey, 'false');
                    this.displayModalToAskForPushPermission();
                }
            }

        } else {
            // Ask the permission only if not asked once
            if (localStorage.getItem(AppConstants.pushPermissionAskedKey) !== 'true') {
                this.displayModalToAskForPushPermission();
            }
        }
    }

    private displayModalToAskForPushPermission(): void {
        if (localStorage.getItem(AppConstants.pushPermissionAskedKey) !== 'true') {

            const modal = this.modalService.open(PushNotificationModalComponent, {centered: true, windowClass: 'larger'});
            modal.result
                .then(result => {
                    if (result === ConfirmModalService.yes) {
                        localStorage.setItem(AppConstants.pushPermissionAskedKey, 'true');
                        this.requestNotificationBrowserPermission();

                    } else if (result === ConfirmModalService.no) {
                        localStorage.setItem(AppConstants.pushPermissionAskedKey, 'true');
                    }

                }, () => { /* catch the rejection */ });
        }
    }

    private async requestNotificationBrowserPermission(): Promise<void> {
        let permission: NotificationPermission;

        try {
            permission = await Notification.requestPermission();
        } catch (error) {
            this.log.error('An error occurred', error);
            this.notificationService.error({title: 'push.notifications.notGranted'});
            return;
        }

        if (permission === 'granted') {
            const existingToken = localStorage.getItem(AppConstants.pushRegistrationTokenKey);
            if (!existingToken) {
                // Display success message only the first time
                this.notificationService.success({title: 'push.notifications.granted'});
            }

            await this.registerToken();

        } else if (permission === 'denied') {
            this.notificationService.warning({
                title: 'push.notifications.blocked.title',
                message: 'push.notifications.blocked.message' });

        } else {
            this.notificationService.error({title: 'push.notifications.notGranted'});
        }
    }

    /**
     * Returns the Firebase Messaging instance, or null when the browser does not support it.
     * Replaces the lazy initialization that @angular/fire used to provide.
     */
    private async getMessagingInstance(): Promise<Messaging | null> {
        if (this.messaging) {
            return this.messaging;
        }

        if (!await isSupported()) {
            return null;
        }

        const app = getApps().length ? getApp() : initializeApp(environment.firebase);
        this.messaging = getMessaging(app);

        return this.messaging;
    }

    private async registerToken(): Promise<void> {
        if (Notification.permission !== 'granted') {
            return;
        }

        let token: string | null = null;

        try {
            const messaging = await this.getMessagingInstance();
            if (messaging) {
                token = await getToken(messaging);
            }
        } catch (error) {
            console.error(error);
            return;
        }

        if (token) {
            const currentToken = localStorage.getItem(AppConstants.pushRegistrationTokenKey);

            if (token !== currentToken) {
                if (this.loggedIn) {
                    if (this.accountEmail) {
                        localStorage.setItem(AppConstants.pushRegistrationEmailKey, this.accountEmail);
                    }

                    this.pushRegistrationService.registerToken(token).pipe().subscribe(_ => {
                        localStorage.setItem(AppConstants.pushRegistrationTokenKey, token);

                        this.subscribeToPushMessages();
                    });

                } else {
                    const email = localStorage.getItem(AppConstants.pushRegistrationEmailKey);
                    if (!!email && email !== "null") {
                        this.pushRegistrationService.registerTokenForVisitor(token, email).pipe().subscribe(_ => {
                            localStorage.setItem(AppConstants.pushRegistrationTokenKey, token);

                            this.subscribeToPushMessages();
                        });
                    }
                }
            } else {
                this.subscribeToPushMessages();
            }
        }
    }

    private async subscribeToPushMessages(): Promise<void> {
        const messaging = await this.getMessagingInstance();
        if (!messaging) {
            return;
        }

        // Avoid stacking listeners when the token is refreshed
        this.pushMessagesUnsubscribe?.();

        // Listen for Push messages. The Firebase SDK fires this callback outside of Angular's
        // zone, hence the explicit ngZone.run() that @angular/fire used to handle.
        this.pushMessagesUnsubscribe = onMessage(messaging, (payload: MessagePayload) => this.ngZone.run(() => {

            if (!!payload && !!payload.data) {

                const notificationOptions = {
                    title: payload.data['CustomData.TitleFR'],
                    body: payload.data['CustomData.BodyFR']
                };

                switch (this.currentLanguage) {
                    case 'de':
                        notificationOptions.title = payload.data['CustomData.TitleDE'];
                        notificationOptions.body = payload.data['CustomData.BodyDE'];
                        break;
                    case 'it':
                        notificationOptions.title = payload.data['CustomData.TitleIT'];
                        notificationOptions.body = payload.data['CustomData.BodyIT'];
                        break;
                    case 'en':
                        notificationOptions.title = payload.data['CustomData.TitleEN'];
                        notificationOptions.body = payload.data['CustomData.BodyEN'];
                        break;
                }

                switch (payload.data['NotificationLevel']) {
                    case 'Warning':
                        this.notificationService.warning(notificationOptions);
                        break;
                    case 'Important':
                        this.notificationService.warning(notificationOptions);
                        break;
                    default:
                        this.notificationService.info(notificationOptions);
                        break;
                }

                this.communicationChannelConfigService.fetchHasUnreadMessage();
            }
        }));
    }

    /**
     * Used to manage the frontend update when necessary (e.g. Breaking changes in API)
     */
    private manageAppUpdate(): void {
        this.updates.versionUpdates.subscribe(event => {
            if (event.type === 'VERSION_DETECTED') {
                this.updates.activateUpdate().then(() => {
                    if (confirm(this.translateService.instant('common.pwa.newAppAvailable'))) {
                        window.location.reload();
                    }
                });
            }
        });

        this.updates.unrecoverable.subscribe(event => {
            console.log(JSON.stringify(event));
            this.notificationService.warning({ title: 'common.pwa.unrecoverableState'});
            window.location.reload();
        });
    }
}
